import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { Button } from '../ui';
import { EyeIcon, PlusIcon, SpinnerIcon, TrashIcon, UploadCloudIcon, XIcon } from '../ui/Icons';
import { FileIcon } from './FileIcon';
import { formatFileSize } from './fileIconUtils';

interface ExistingFile {
  id: number;
  fileName?: string;
  fileSize?: number;
}

interface FileAttachmentsFormBlockProps {
  newFiles: File[];
  existingFiles: ExistingFile[];
  onNewFilesChange: (files: File[]) => void;
  onRemoveExisting: (fileId: number) => void;
  getViewFileUrl?: (fileId: number) => string;
  label?: string;
  hint?: string;
  accept?: string;
  readOnly?: boolean;
  /** Tighter layout for dense forms (e.g. journal edit). */
  variant?: 'default' | 'compact' | 'panel';
}

type AttachmentRow =
  | { kind: 'existing'; id: number; fileName: string; fileSize?: number }
  | { kind: 'new'; index: number; file: File };

export const FileAttachmentsFormBlock: React.FC<FileAttachmentsFormBlockProps> = ({
  newFiles,
  existingFiles,
  onNewFilesChange,
  onRemoveExisting,
  getViewFileUrl,
  label = 'Attachments',
  hint,
  accept = '*/*',
  readOnly = false,
  variant = 'default',
}) => {
  const { colors } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [viewingExistingId, setViewingExistingId] = useState<number | null>(null);

  const mergeNewFiles = useCallback(
    (added: File[]) => {
      if (added.length === 0) return;
      onNewFilesChange([...newFiles, ...added]);
    },
    [newFiles, onNewFilesChange],
  );

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    mergeNewFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!readOnly && e.dataTransfer.types.includes('Files')) setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (!readOnly) mergeNewFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeNew = (index: number) => {
    onNewFilesChange(newFiles.filter((_, i) => i !== index));
  };

  const openBlobInNewTab = useCallback((blob: Blob) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
    if (win) {
      const revoke = () => window.URL.revokeObjectURL(blobUrl);
      win.addEventListener('beforeunload', revoke, { once: true });
      window.setTimeout(revoke, 120_000);
    } else {
      window.URL.revokeObjectURL(blobUrl);
    }
  }, []);

  const handleViewNewFile = useCallback((file: File) => openBlobInNewTab(file), [openBlobInNewTab]);

  const handleViewExistingFile = useCallback(
    async (fileId: number) => {
      if (!getViewFileUrl) return;
      setViewingExistingId(fileId);
      try {
        const response = await api.get(getViewFileUrl(fileId), { responseType: 'blob' });
        const raw = response.data as Blob;
        const ct = (response.headers['content-type'] as string | undefined) || raw.type || 'application/octet-stream';
        openBlobInNewTab(raw.type ? raw : new Blob([raw], { type: ct }));
      } catch (err) {
        console.error('View file failed:', err);
      } finally {
        setViewingExistingId(null);
      }
    },
    [getViewFileUrl, openBlobInNewTab],
  );

  const rows = useMemo<AttachmentRow[]>(() => {
    const existing = existingFiles.map((file) => ({
      kind: 'existing' as const,
      id: file.id,
      fileName: file.fileName || 'Unnamed',
      fileSize: file.fileSize,
    }));
    const pending = newFiles.map((file, index) => ({ kind: 'new' as const, index, file }));
    return [...existing, ...pending];
  }, [existingFiles, newFiles]);

  const totalCount = rows.length;
  const pendingCount = newFiles.length;
  const compact = variant === 'compact';
  const panel = variant === 'panel';
  const openFilePicker = () => inputRef.current?.click();

  const iconBtn = (className?: string) =>
    cn('inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors', colors.textMuted, colors.interactiveHover, className);

  const renderPanelRow = (row: AttachmentRow) => {
    if (row.kind === 'existing') {
      const isViewing = viewingExistingId === row.id;
      return (
        <li
          key={`existing-${row.id}`}
          className={cn(
            'group flex items-center gap-3 border-l-[3px] border-l-emerald-500 px-4 py-2.5',
            colors.interactiveHover,
          )}
        >
          <FileIcon fileName={row.fileName} size="sm" />
          <div className="min-w-0 flex-1">
            <p className={cn('truncate text-sm font-medium', colors.text)} title={row.fileName}>
              {row.fileName}
            </p>
            <p className={cn('text-xs', colors.textMuted)}>
              Saved{row.fileSize != null ? ` · ${formatFileSize(row.fileSize)}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
            {getViewFileUrl && (
              <button
                type="button"
                disabled={isViewing}
                onClick={() => handleViewExistingFile(row.id)}
                className={iconBtn()}
                title="View"
                aria-label="View file"
              >
                {isViewing ? <SpinnerIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => onRemoveExisting(row.id)}
                className={iconBtn(colors.error)}
                title="Remove"
                aria-label="Remove file"
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </li>
      );
    }

    const { file, index } = row;
    return (
      <li
        key={`new-${index}-${file.name}`}
        className="group flex items-center gap-3 border-l-[3px] border-l-amber-500 bg-amber-50/35 px-4 py-2.5 dark:bg-amber-950/20"
      >
        <FileIcon fileName={file.name} size="sm" />
        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-sm font-medium', colors.text)} title={file.name}>
            {file.name}
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Pending upload · {formatFileSize(file.size)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button type="button" onClick={() => handleViewNewFile(file)} className={iconBtn()} title="Preview" aria-label="Preview file">
            <EyeIcon className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => removeNew(index)} className={iconBtn(colors.error)} title="Remove" aria-label="Remove file">
            <XIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </li>
    );
  };

  if (panel) {
    return (
      <div
        className={cn(
          'overflow-hidden rounded-xl border',
          colors.border,
          colors.surface,
          dragActive && !readOnly && 'ring-2 ring-indigo-400/45',
        )}
        onDragEnter={readOnly ? undefined : handleDragEnter}
        onDragOver={readOnly ? undefined : handleDragOver}
        onDragLeave={readOnly ? undefined : handleDragLeave}
        onDrop={readOnly ? undefined : handleDrop}
      >
        <div className={cn('flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3', colors.border)}>
          <div className="min-w-0">
            <p className={cn('text-sm font-semibold', colors.text)}>
              {label || 'Supporting documents'}
            </p>
            {hint && <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{hint}</p>}
          </div>
          {!readOnly && (
            <>
              <input ref={inputRef} type="file" multiple onChange={handleAddFiles} className="hidden" accept={accept} />
              <Button type="button" variant="outline" size="sm" onClick={openFilePicker} className="shrink-0">
                <UploadCloudIcon className="mr-1.5 h-4 w-4" />
                Upload file
              </Button>
            </>
          )}
        </div>

        {rows.length === 0 ? (
          readOnly ? (
            <div className={cn('px-4 py-8 text-center text-sm', colors.textMuted)}>No attachments</div>
          ) : (
            <button
              type="button"
              onClick={openFilePicker}
              className={cn(
                'flex w-full flex-col items-center justify-center px-4 py-10 text-center transition-colors',
                colors.interactiveHover,
                dragActive && 'bg-indigo-50/60 dark:bg-indigo-950/25',
              )}
            >
              <div className={cn('mb-2 flex h-10 w-10 items-center justify-center rounded-full', colors.info)}>
                <UploadCloudIcon className="h-5 w-5" />
              </div>
              <span className={cn('text-sm font-medium', colors.text)}>
                {dragActive ? 'Drop files here' : 'No documents attached'}
              </span>
              <span className={cn('mt-1 text-xs', colors.textMuted)}>
                Click to browse or drag files into this panel
              </span>
            </button>
          )
        ) : (
          <>
            <ul className={cn('divide-y', colors.border)}>{rows.map(renderPanelRow)}</ul>
            {!readOnly && (
              <button
                type="button"
                onClick={openFilePicker}
                className={cn(
                  'flex w-full items-center justify-center gap-2 border-t px-4 py-2.5 text-xs font-medium transition-colors',
                  colors.border,
                  colors.textMuted,
                  colors.interactiveHover,
                  dragActive && 'bg-indigo-50/60 text-indigo-700 dark:bg-indigo-950/25 dark:text-indigo-300',
                )}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                {dragActive ? 'Drop to add more files' : 'Add another file'}
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={cn('space-y-1.5', dragActive && !readOnly && 'rounded-lg ring-1 ring-indigo-400/60')}
        onDragEnter={readOnly ? undefined : handleDragEnter}
        onDragOver={readOnly ? undefined : handleDragOver}
        onDragLeave={readOnly ? undefined : handleDragLeave}
        onDrop={readOnly ? undefined : handleDrop}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {label && <span className={cn('text-sm font-medium', colors.text)}>{label}</span>}
          {totalCount > 0 && (
            <span className={cn('text-xs', colors.textMuted)}>
              {totalCount} file{totalCount !== 1 ? 's' : ''}
              {pendingCount > 0 ? ` · ${pendingCount} pending` : ''}
            </span>
          )}
          {!readOnly && (
            <>
              <input ref={inputRef} type="file" multiple onChange={handleAddFiles} className="hidden" accept={accept} />
              <Button type="button" variant="ghost" size="sm" onClick={openFilePicker} className="h-7 px-2 text-xs">
                <PlusIcon className="mr-1 h-3.5 w-3.5" />
                Add file
              </Button>
            </>
          )}
          {hint && totalCount === 0 && (
            <span className={cn('text-xs', colors.textMuted)}>{hint}</span>
          )}
        </div>

        {rows.length > 0 && (
          <ul className="space-y-1">
            {rows.map((row) => {
              if (row.kind === 'existing') {
                const isViewing = viewingExistingId === row.id;
                return (
                  <li
                    key={`existing-${row.id}`}
                    className={cn('flex items-center gap-2 rounded-md border px-2 py-1', colors.border, colors.surface)}
                  >
                    <FileIcon fileName={row.fileName} size="sm" />
                    <button
                      type="button"
                      disabled={!getViewFileUrl || isViewing}
                      onClick={() => handleViewExistingFile(row.id)}
                      className={cn('min-w-0 flex-1 truncate text-left text-sm', colors.text, getViewFileUrl && colors.interactiveHover)}
                      title={row.fileName}
                    >
                      {row.fileName}
                    </button>
                    {row.fileSize != null && (
                      <span className={cn('shrink-0 text-xs tabular-nums', colors.textMuted)}>{formatFileSize(row.fileSize)}</span>
                    )}
                    {getViewFileUrl && (
                      <button
                        type="button"
                        disabled={isViewing}
                        onClick={() => handleViewExistingFile(row.id)}
                        className={iconBtn()}
                        title="View"
                        aria-label="View file"
                      >
                        {isViewing ? <SpinnerIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => onRemoveExisting(row.id)}
                        className={iconBtn(colors.error)}
                        title="Remove"
                        aria-label="Remove file"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </li>
                );
              }

              const { file, index } = row;
              return (
                <li
                  key={`new-${index}-${file.name}`}
                  className="flex items-center gap-2 rounded-md border border-amber-200/70 bg-amber-50/30 px-2 py-1 dark:border-amber-800/40 dark:bg-amber-950/15"
                >
                  <FileIcon fileName={file.name} size="sm" />
                  <button
                    type="button"
                    onClick={() => handleViewNewFile(file)}
                    className={cn('min-w-0 flex-1 truncate text-left text-sm', colors.text, colors.interactiveHover)}
                    title={file.name}
                  >
                    {file.name}
                  </button>
                  <span className={cn('shrink-0 text-[10px] font-medium uppercase text-amber-700 dark:text-amber-300')}>New</span>
                  <span className={cn('shrink-0 text-xs tabular-nums', colors.textMuted)}>{formatFileSize(file.size)}</span>
                  <button type="button" onClick={() => handleViewNewFile(file)} className={iconBtn()} title="Preview" aria-label="Preview file">
                    <EyeIcon className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => removeNew(index)} className={iconBtn(colors.error)} title="Remove" aria-label="Remove file">
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {totalCount === 0 && readOnly && (
          <p className={cn('text-xs', colors.textMuted)}>No attachments</p>
        )}
      </div>
    );
  }

  return (
    <div
      className="space-y-3"
      onDragEnter={readOnly ? undefined : handleDragEnter}
      onDragOver={readOnly ? undefined : handleDragOver}
      onDragLeave={readOnly ? undefined : handleDragLeave}
      onDrop={readOnly ? undefined : handleDrop}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {label && <h3 className={cn('text-sm font-semibold', colors.text)}>{label}</h3>}
            {totalCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {totalCount} file{totalCount !== 1 ? 's' : ''}
              </span>
            )}
            {pendingCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {pendingCount} pending
              </span>
            )}
          </div>
          {hint && <p className={cn('mt-1 text-xs leading-relaxed', colors.textMuted)}>{hint}</p>}
        </div>
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" onClick={openFilePicker} className="shrink-0">
            <UploadCloudIcon className="mr-1.5 h-4 w-4" />
            Add files
          </Button>
        )}
      </div>

      {!readOnly && (
        <input ref={inputRef} type="file" multiple onChange={handleAddFiles} className="hidden" accept={accept} />
      )}

      {totalCount === 0 && readOnly ? (
        <div className={cn('rounded-xl border border-dashed px-5 py-8 text-center', colors.border, colors.surfaceHover)}>
          <p className={cn('text-sm font-medium', colors.text)}>No attachments</p>
          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>Files added to this record will appear here</p>
        </div>
      ) : totalCount === 0 && !readOnly ? (
        <button
          type="button"
          onClick={openFilePicker}
          className={cn(
            'flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
            colors.border,
            colors.surfaceHover,
            colors.interactiveHover,
            dragActive && 'border-indigo-400 bg-indigo-50/50 ring-2 ring-indigo-500/40 dark:border-indigo-500 dark:bg-indigo-950/30',
          )}
        >
          <div className={cn('mb-3 flex h-12 w-12 items-center justify-center rounded-full', dragActive ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : colors.info)}>
            <UploadCloudIcon className="h-6 w-6" />
          </div>
          <span className={cn('text-sm font-medium', colors.text)}>
            {dragActive ? 'Release to attach files' : 'No attachments yet'}
          </span>
          <span className={cn('mt-1 max-w-sm text-xs leading-relaxed', colors.textMuted)}>
            Click here or drag files into this area. Uploads are applied when you save.
          </span>
        </button>
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3',
            dragActive && !readOnly && 'rounded-xl ring-2 ring-indigo-500/50 ring-offset-2 ring-offset-transparent',
          )}
        >
          {rows.map((row) => {
            if (row.kind === 'existing') {
              const isViewing = viewingExistingId === row.id;
              return (
                <article key={`existing-${row.id}`} className={cn('group relative flex min-w-0 flex-col rounded-xl border p-3 shadow-sm transition-shadow', colors.border, colors.surface, getViewFileUrl && 'hover:shadow-md')}>
                  <div className="flex items-start gap-3">
                    <FileIcon fileName={row.fileName} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn('truncate text-sm font-medium', colors.text)} title={row.fileName}>{row.fileName}</p>
                        <span className="shrink-0 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Saved</span>
                      </div>
                      {row.fileSize != null && <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{formatFileSize(row.fileSize)}</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-1 border-t pt-2 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                    {getViewFileUrl && (
                      <Button type="button" variant="ghost" size="sm" disabled={isViewing} onClick={() => handleViewExistingFile(row.id)} className="h-8 px-2">
                        {isViewing ? <SpinnerIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        <span className="ml-1.5 hidden sm:inline">View</span>
                      </Button>
                    )}
                    {!readOnly && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveExisting(row.id)} className={cn('h-8 px-2', colors.error)}>
                        <TrashIcon className="h-4 w-4" />
                        <span className="ml-1.5 hidden sm:inline">Remove</span>
                      </Button>
                    )}
                  </div>
                </article>
              );
            }

            const { file, index } = row;
            return (
              <article key={`new-${index}-${file.name}`} className={cn('group relative flex min-w-0 flex-col rounded-xl border p-3 shadow-sm transition-shadow border-amber-200/80 bg-amber-50/40 dark:border-amber-800/50 dark:bg-amber-950/20', colors.surface)}>
                <div className="flex items-start gap-3">
                  <FileIcon fileName={file.name} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn('truncate text-sm font-medium', colors.text)} title={file.name}>{file.name}</p>
                      <span className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">Pending</span>
                    </div>
                    <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end gap-1 border-t border-amber-200/60 pt-2 dark:border-amber-800/40">
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleViewNewFile(file)} className="h-8 px-2">
                    <EyeIcon className="h-4 w-4" />
                    <span className="ml-1.5 hidden sm:inline">Preview</span>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeNew(index)} className={cn('h-8 px-2', colors.error)}>
                    <XIcon className="h-4 w-4" />
                    <span className="ml-1.5 hidden sm:inline">Remove</span>
                  </Button>
                </div>
              </article>
            );
          })}
          {!readOnly && (
            <button
              type="button"
              onClick={openFilePicker}
              className={cn(
                'flex min-h-[7.5rem] flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors',
                colors.border,
                colors.surfaceHover,
                colors.interactiveHover,
                dragActive && 'border-indigo-400 bg-indigo-50/50 dark:border-indigo-500 dark:bg-indigo-950/30',
              )}
            >
              <div className={cn('mb-2 flex h-10 w-10 items-center justify-center rounded-full', dragActive ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : colors.info)}>
                {dragActive ? <UploadCloudIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
              </div>
              <span className={cn('text-sm font-medium', colors.text)}>{dragActive ? 'Drop files here' : 'Add another file'}</span>
              <span className={cn('mt-0.5 text-xs', colors.textMuted)}>Click or drag files</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileAttachmentsFormBlock;
